import { useCallback, useEffect, useState } from 'react';
import styles from './StatsSection.module.css';
import { get } from '../../helpers/api';
import { ActivationChartModal } from '../ActivationChartModal/ActivationChartModal';

type ActivationCounts = {
  today: number;
  yesterday: number;
  thisWeek: number;
  thisMonth: number;
  lastMonth: number;
  todayChange?: number | null;
  yesterdayChange?: number | null;
  thisWeekChange?: number | null;
  thisMonthChange?: number | null;
  lastMonthChange?: number | null;
};

type StatKey = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth' | 'lastMonth';

type Stat = {
  label: string;
  key: StatKey;
  value: number;
  change?: number | null;
};

const COMPARE_MAP: Partial<Record<StatKey, StatKey>> = {
  today: 'yesterday',
  thisMonth: 'lastMonth',
};

const CHANGE_KEY_MAP: Record<StatKey, keyof ActivationCounts> = {
  today: 'todayChange',
  yesterday: 'yesterdayChange',
  thisWeek: 'thisWeekChange',
  thisMonth: 'thisMonthChange',
  lastMonth: 'lastMonthChange',
};

const getChangeClass = (change?: number | null) =>
  change == null
    ? styles.changeNew
    : change > 0
      ? styles.changeUp
      : change < 0
        ? styles.changeDown
        : styles.changeFlat;

const TrendIcon = ({ dir }: { dir: 'up' | 'down' }) => (
  <svg viewBox="0 0 24 24" className={styles.trendIcon} aria-hidden="true">
    {dir === 'up' ? (
      <>
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </>
    ) : (
      <>
        <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
        <polyline points="17 18 23 18 23 12" />
      </>
    )}
  </svg>
);

const StatIcon = ({ type }: { type: StatKey }) => {
  switch (type) {
    case 'today':
      return (
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
          <path
            d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'yesterday':
      return (
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M5.5 5.5L8 6.5 7 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'thisWeek':
      return (
        <svg viewBox="0 0 24 24">
          <rect x="2" y="5" width="20" height="14" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
          <circle cx="5" cy="12" r="1.4" fill="currentColor" />
          <circle cx="8.5" cy="12" r="1.4" fill="currentColor" />
          <circle cx="12" cy="12" r="1.4" fill="currentColor" />
          <circle cx="15.5" cy="12" r="1.4" fill="currentColor" />
          <circle cx="19" cy="12" r="1.4" fill="currentColor" />
          <circle cx="8.5" cy="9" r="1.2" fill="currentColor" opacity="0.45" />
          <circle cx="15.5" cy="15" r="1.2" fill="currentColor" opacity="0.45" />
        </svg>
      );
    case 'thisMonth':
      return (
        <svg viewBox="0 0 24 24">
          <rect x="2" y="4" width="20" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
          <rect x="5" y="7" width="14" height="10" rx="2" fill="currentColor" />
        </svg>
      );
    case 'lastMonth':
      return (
        <svg viewBox="0 0 24 24">
          <rect x="2" y="4" width="20" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M2 9h20" stroke="currentColor" strokeWidth="2" />
          <path d="M9 14l3-3v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 14h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
  }
};

const STAT_COLOR: Record<StatKey, string> = {
  today: 'blue',
  yesterday: 'orange',
  thisWeek: 'purple',
  thisMonth: 'green',
  lastMonth: 'blue',
};

const PERIOD_MAP: Record<StatKey, number> = {
  today: 1,
  yesterday: 2,
  thisWeek: 3,
  thisMonth: 4,
  lastMonth: 5,
};

const STAT_META: Array<Pick<Stat, 'label' | 'key'>> = [
  { label: '今天', key: 'today' },
  { label: '昨天', key: 'yesterday' },
  { label: '7天', key: 'thisWeek' },
  { label: '本月', key: 'thisMonth' },
  { label: '上月', key: 'lastMonth' },
];

export const StatsSection = () => {
  const [stats, setStats] = useState<Stat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [spinning, setSpinning] = useState(false);

  const handleRefresh = () => {
    setSpinning(true);
    setRefreshKey((key) => key + 1);
    setTimeout(() => setSpinning(false), 600);
  };

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const counts = await get<ActivationCounts>('/admin/tel/stats');
      setStats(STAT_META.map(({ label, key }) => {
        const value = Number(counts[key]) || 0;
        const changeKey = CHANGE_KEY_MAP[key];
        const apiChange = counts[changeKey];
        const prevKey = COMPARE_MAP[key];
        const prev = prevKey ? Number(counts[prevKey]) || 0 : 0;
        let change: number | null | undefined;
        if (apiChange !== undefined) {
          change = apiChange !== null ? Number(apiChange) : value > 0 ? null : 0;
        } else if (prevKey) {
          change = prev > 0 ? (value - prev) / prev * 100 : value > 0 ? null : 0;
        }
        return { label, key, value, change };
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats, refreshKey]);

  return (
    <div className={`card ${styles.statsCard}`}>
      <div className={`card-header ${styles.compactHeader}`}>
        <div className="section-title">
          <div className="icon-box">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 19V9" />
              <path d="M10 19V5" />
              <path d="M16 19v-8" />
              <path d="M22 19V3" />
            </svg>
          </div>
          数据概览
        </div>
        <button type="button" className="card-refresh" onClick={handleRefresh}>
          <svg
            className={spinning ? 'card-refresh-spin' : ''}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <path d="M21 3v5h-5" />
          </svg>
        </button>
      </div>
      <div className={`${styles.grid} ${loading ? styles.loading : ''} ${spinning ? styles.refreshing : ''}`}>
        {stats.map((stat) => (
          <div
            key={stat.key}
            className={`${styles.item} ${styles.clickable}`}
            onClick={() => setSelectedPeriod(PERIOD_MAP[stat.key])}
          >
            <div className={`stat-icon ${STAT_COLOR[stat.key]}`}>
              <StatIcon type={stat.key} />
            </div>
            <div className={styles.label}>{stat.label}</div>
            <div className={styles.value}>{stat.value.toLocaleString()}</div>
            {stat.change !== undefined && (
              <div className={`${styles.change} ${getChangeClass(stat.change)}`}>
                {stat.change === null && '--'}
                {stat.change === 0 && '-'}
                {stat.change !== null && stat.change > 0 && <TrendIcon dir="up" />}
                {stat.change !== null && stat.change < 0 && <TrendIcon dir="down" />}
                {stat.change !== null && stat.change !== 0 && (
                  <span>{Math.abs(stat.change).toFixed(1)}%</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {error && <div className="error">{error}</div>}
      <ActivationChartModal
        open={selectedPeriod !== null}
        onOpenChange={(next) => {
          if (!next) {
            setSelectedPeriod(null);
          }
        }}
        period={selectedPeriod ?? 1}
      />
    </div>
  );
};